import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
// Components
import {
  SidebarBox,
  Sidebar,
  Button,
  Tag,
  FormTitle,
  Skeleton,
} from '@zolteam/zol-admin-ui';
import { FormElements } from '@zolteam/zol-react-form';
import { useEffect, useMemo, useState } from 'react';
import FormSkeleton from './FormSkeleton';
import ContentTranslationStatus from '../../../../components/cortizol/molecules/ContentTranslationStatus/ContentTranslationStatus';
import ContentTranslationLanguage from '../../../../components/cortizol/molecules/ContentTranslationLanguage/ContentTranslationLanguage';
import ContentTranslationHistory from '../../../../components/cortizol/molecules/ContentTranslationHistory/ContentTranslationHistory';
// Permissions
import permissions from '../../../../permissions/permissionManagement';
import ProtectedFeature from '../../../../permissions/ProtectedFeature';
// Services
import { useGetContentTypeQuery } from '../../../../services/cortizol/content-type/contentTypeQueries';
import { useGetContentQuery } from '../../../../services/cortizol/content/contentQueries';
import { getAllContents, getContent, updateContent } from '../../../../services/cortizol/content/contentService';
import {
  getAllMedia, getMedia, postArchiveMedia, postMedia,
} from '../../../../services/cortizol/media/mediaService';
import {
  deleteMediaFolder,
  getMyMediaTree, postMediaFolder, postMoveMediaFolder, updateMediaFolder,
} from '../../../../services/cortizol/media-tree/mediaTreeService';
import { getAlllocales } from '../../../../services/cortizol/locale/localeService';
import { useGetAllContentTranslationsDataVersionQuery } from '../../../../services/cortizol/content-translation/contentTranslationQueries';
import { postPublishContent, postWithdrawContent, updateContentTranslation } from '../../../../services/cortizol/content-translation/contentTranslationService';
import { postUploadMedia } from '../../../../services/uploadMedia/uploadMediaService';
// Hooks
import useContentTranslation from '../../../../domain/cortizol/content-translation/useContentTranslation';
import useContent from '../../../../domain/cortizol/content/useContent';
import useLocale from '../../../../domain/cortizol/locale/useLocale';
// Context
import { useAppContext } from '../../../../store/AppContext';
// Localisation
import fr from '../../../../locales/fr.json';
// Style
import styles from './ContentTypeForm.module.css';

/**
 * Form component.
 * @param {object} props
 * @param {object} props.formConfig
 * @param {object} props.formInfo
 * @returns {JSX.Element}
 */
function Form({ formConfig, formInfo }) {
  // Hooks
  const { t } = useTranslation();
  const { contentTypeId, contentId } = useParams();
  const {
    contentTranslationStatusOptions,
    TRANSLATIONS_STATUSES,
    getContentTranslationStatusUIConfig,
    switchFormContentTranslation,
    switchContentTranslationVersion,
    saveContentTranslation,
    publishOrWithdrawContentTranslation,
  } = useContentTranslation();
  const { getTranslationFromContent } = useContent();
  const { currentLocale, localesOptions, defaultLocale } = useLocale();
  const [, dispatch] = useAppContext();

  // States and variables
  const [newContentTranslationStatus, setNewContentTranslationStatus] = useState(
    TRANSLATIONS_STATUSES.draft,
  );
  const [selectedVersion, setSelectedVersion] = useState(null);

  // Services
  const {
    data: dataContentType,
    isLoading: isLoadingContentType,
  } = useGetContentTypeQuery(contentTypeId);
  const {
    data: dataContent,
    isLoading: isContentLoading,
    refetch: refetchContent,
  } = useGetContentQuery(contentId);
  const contentTranslation = useMemo(() => {
    if (dataContent && currentLocale) {
      return getTranslationFromContent(dataContent, currentLocale);
    }
    return null;
  }, [dataContent, currentLocale]);
  const {
    data: dataContentTranslationVersions,
    isLoading: isLoadingContentTranslationVersions,
  } = useGetAllContentTranslationsDataVersionQuery(
    !!dataContent && !!currentLocale
      ? contentTranslation.id
      : undefined,
    { pageNumber: 1, pageSize: 999 },
    { enabled: !!dataContent && !!currentLocale },
  );
  const updateContentMutation = useMutation({
    mutationFn: (/** @type {string} */updatedContent) => updateContent(
      contentId,
      { name: updatedContent },
      dataContent.version,
    ),
    onSuccess: () => {
      dispatch({
        type: 'SET_MESSAGE',
        payload: { text: t('cortizol.contentTypeForm.updateTitleSuccess'), type: 'success' },
      });
      refetchContent();
    },
  });
  const postPublishContentMutation = useMutation({
    mutationFn: () => postPublishContent(contentTranslation.id, contentTranslation.version + 1),
    onSuccess: () => {
      dispatch({
        type: 'SET_MESSAGE',
        payload: { text: t('cortizol.contentTypeForm.publishContentTranslationSuccess'), type: 'success' },
      });
      refetchContent();
    },
  });
  const postWithdrawContentMutation = useMutation({
    mutationFn: () => postWithdrawContent(contentTranslation.id, contentTranslation.version + 1),
    onSuccess: () => {
      dispatch({
        type: 'SET_MESSAGE',
        payload: { text: t('cortizol.contentTypeForm.withdrawContentTranslationSuccess'), type: 'success' },
      });
      refetchContent();
    },
  });
  const updateContentTranslationMutation = useMutation({
    mutationFn: (updatedContentTranslation) => updateContentTranslation(
      contentTranslation.id,
      { data: updatedContentTranslation },
      contentTranslation.version,
    ),
    onSuccess: () => {
      dispatch({
        type: 'SET_MESSAGE',
        payload: { text: t('cortizol.contentTypeForm.updateContentTranslationSuccess'), type: 'success' },
      });
      publishOrWithdrawContentTranslation(
        postPublishContentMutation.mutate,
        postWithdrawContentMutation.mutate,
        newContentTranslationStatus,
        contentTranslation.status,
      );
      refetchContent();
    },
  });

  // Effects
  useEffect(() => {
    if (dataContent) {
      setNewContentTranslationStatus(contentTranslation.status);
    }
  }, [dataContent]);

  // Handlers
  /**
   * Handle on title change.
   * @param {string} title
   * @returns {void}
   */
  const handleOnTitleChange = (title) => {
    updateContentMutation.mutate(title);
  };
  /**
   * Handle content status change.
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   */
  const handleContentStatusChange = (event) => {
    const newStatus = /** @type {ContentTranslationStatus} */(event.target.value);
    setNewContentTranslationStatus(newStatus);
  };
  /**
   * Handle language change.
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   */
  const handleLocaleChange = (event) => {
    const newLocale = event.target.value;

    switchFormContentTranslation(
      dataContent,
      newLocale,
      defaultLocale,
      formInfo.formMethods.reset,
      formInfo.formMethods.formState.isDirty,
    );
  };
  /**
   * Handle version change.
   * @param {ContentTranslationDataVersion} version
   * @returns {void}
   */
  const handleVersionChange = (version) => {
    setSelectedVersion(version.timestamp);
    switchContentTranslationVersion(
      version,
      formInfo.formMethods.reset,
    );
  };
  /**
   * Handle form submit.
   * @returns {Promise<void>}
   */
  const handleFormSubmit = async () => saveContentTranslation(
    formInfo.formMethods.getValues(),
    newContentTranslationStatus,
    contentTranslation.status,
    formInfo.formMethods.trigger,
    (newValues) => updateContentTranslationMutation.mutate(newValues),
    formInfo.formSanitizer,
  );

  return (
    <SidebarBox sidebar={(
      <Sidebar>
        <ContentTranslationStatus
          statusListOptions={contentTranslationStatusOptions}
          onStatusChange={handleContentStatusChange}
          editedStatus={newContentTranslationStatus}
          sectionTitle={t('cortizol.contentTypeForm.status.sectionTitle')}
          statusTitle={t('cortizol.contentTypeForm.status.statusTitle')}
          selectStatusPlaceholder={t('cortizol.contentTypeForm.status.selectStatusPlaceholder')}
          isLoading={isContentLoading}
          statusTag={dataContent && currentLocale ? (
            <Tag
              label={t(getContentTranslationStatusUIConfig(
                contentTranslation.status,
              ).label)}
              color={getContentTranslationStatusUIConfig(
                contentTranslation.status,
              ).color}
            />
          ) : null}
        />
        <ContentTranslationLanguage
          isloading={!currentLocale}
          localesOptions={localesOptions}
          currentLocale={currentLocale}
          handleLocaleChange={handleLocaleChange}
          sectionTitle={t('cortizol.contentTypeForm.language.sectionTitle')}
          selectLocalePlaceholder={t('cortizol.contentTypeForm.language.selectPlaceholder')}
        />
        <ContentTranslationHistory
          isLoading={isLoadingContentTranslationVersions}
          contentTranslationVersions={dataContentTranslationVersions?.data}
          onVersionChange={handleVersionChange}
          selectedVersion={selectedVersion}
          sectionTitle={t('cortizol.contentTypeForm.history.sectionTitle')}
          currentVersionLabel={t('cortizol.contentTypeForm.history.currentVersionLabel')}
          sectionInstructionlabel={t('cortizol.contentTypeForm.history.sectionInstructionlabel')}
        />
        <ProtectedFeature permissions={permissions.contentTypeForm.savePublishContent}>
          <Button
            form={formInfo.formName}
            isLoading={updateContentTranslationMutation.isLoading}
            onClick={handleFormSubmit}
            color={getContentTranslationStatusUIConfig(newContentTranslationStatus).backgroundColor}
          >
            {t(getContentTranslationStatusUIConfig(newContentTranslationStatus).actionLabel)}
          </Button>
        </ProtectedFeature>
      </Sidebar>
      )}
    >
      <div className="padding-l">
        {isLoadingContentType || isContentLoading
          ? <Skeleton shape="rectangle" width="30%" height="2.5rem" className="margin-top-m margin-bottom-m" />
          : (
            <FormTitle
              title={dataContentType.schema.title}
              editableTitle={{
                label: dataContent.name || '',
                onSave: handleOnTitleChange,
              }}
              className={styles['form-title']}
            />
          )}

        {isLoadingContentType || isContentLoading
          ? <FormSkeleton />
          : (
            <FormElements
              formTitle={formInfo.formName}
              formConfig={formConfig}
              asyncCallbacks={{
                asyncGetContentsList: getAllContents,
                asyncGetContent: getContent,
              }}
              asyncMediaCallbacks={{
                getMedia,
                postMedia,
                uploadMedia: postUploadMedia,
                archiveMedia: postArchiveMedia,
                getLocales: getAlllocales,
                getMediasList: getAllMedia,
                deleteMediaFolder,
                getMediaTree: getMyMediaTree,
                postAddMediaFolder: postMediaFolder,
                postMoveMediaFolder,
                updateMediaFolder,
              }}
              translations={fr.cortizol}
            />
          )}
      </div>
    </SidebarBox>

  );
}

export default Form;
